import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  ArrowLeft,
  AlertCircle,
  CheckCircle2,
  Lock,
  Loader2,
  RotateCw,
  Send,
  ExternalLink,
} from 'lucide-react';
import { api } from '@/api/client';
import type {
  GetResourcesResp,
  ManualRequestReq,
  ManualRequestResp,
  ManualRequestResponse,
  Resource,
  ResourceType,
} from '@/api/types';
import { cn } from '@/lib/utils';
import { parseMemoryBytes, formatBytes } from '@/lib/resource-helpers';
import Section from '@/components/resources/form/Section';
import Field from '@/components/resources/form/Field';
import { inputClass, textareaClass } from '@/components/resources/form/styles';
import { extractApiMessage } from '@/components/resources/form/errors';

const DEFAULT_USER = 'admin';
const DEFAULT_PROJECT = 'manual-request';
const DEFAULT_DEPARTMENT = 'operations';
// Start the amount empty rather than "0": a pre-filled 0 has to be cleared
// before typing and fails the "> 0" check, so the field opens flagged red.
const DEFAULT_VALUE = '';

// Mirror pkg/ui/manualresourcerequestformhandler.go:39 — the legacy form
// prefills the cluster ID as "manual-<UnixNano>". JS only has millisecond
// precision; the trailing zeros preserve the same string shape so anything
// downstream that parses the suffix still sees a nanosecond-width number.
function defaultClusterId(): string {
  return `manual-${Date.now()}000000`;
}

export default function ManualRequestPage() {
  const { name: nameParam = '' } = useParams<{ name: string }>();

  const {
    data,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['resource', nameParam],
    queryFn: () =>
      api<GetResourcesResp>(`/v1/resources/${encodeURIComponent(nameParam)}`),
    enabled: nameParam !== '',
  });

  const resource = data?.resources?.[0];
  const type = resource?.type as ResourceType | undefined;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-sm text-muted-foreground"
        >
          <Link
            to="/resources"
            className="hover:text-foreground transition-colors inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Resources
          </Link>
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          <span className="text-foreground truncate max-w-[40ch]">
            {nameParam}
          </span>
          <ChevronRight className="h-3.5 w-3.5 opacity-60" aria-hidden />
          <span className="text-foreground">Manual request</span>
        </nav>
        <h1 className="text-2xl font-semibold">
          Manual Request
          <span className="ml-2 font-mono text-base text-muted-foreground">
            {nameParam}
          </span>
        </h1>
        <p className="text-sm text-muted-foreground">
          Administrator allocation. Bypasses the gRPC scheduler — the
          allocation is recorded as a regular usage row, and the same quota
          and soft-limit checks apply.
        </p>
      </div>

      {isLoading && (
        <div className="py-12 text-center text-muted-foreground text-sm">
          Loading resource…
        </div>
      )}

      {fetchError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Failed to load resource</div>
            <div className="text-xs mt-0.5 opacity-90">
              {extractApiMessage(fetchError)}
            </div>
          </div>
        </div>
      )}

      {resource && type === 'String' && <StringBlockedNotice />}

      {resource && type && type !== 'String' && (
        <RequestForm resource={resource} type={type} />
      )}
    </div>
  );
}

function StringBlockedNotice() {
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive flex items-start gap-2">
      <Lock className="h-5 w-5 shrink-0 mt-0.5" />
      <div>
        <div className="font-medium">String resources are not allocatable</div>
        <div className="text-xs mt-1 opacity-90">
          A String resource returns the same literal value to every request,
          so there is nothing to allocate. The server refuses manual requests
          for this type (see{' '}
          <code className="font-mono">
            internal/db/resource/resourceDB.go:440
          </code>
          ).
        </div>
        <div className="mt-3">
          <Link
            to="/resources"
            className="inline-flex items-center gap-1 text-xs underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Resources
          </Link>
        </div>
      </div>
    </div>
  );
}

interface RequestFormProps {
  resource: Resource;
  type: ResourceType;
}

function RequestForm({ resource, type }: RequestFormProps) {
  const queryClient = useQueryClient();

  const [value, setValue] = useState(DEFAULT_VALUE);
  const [clusterId, setClusterId] = useState(() => defaultClusterId());
  const [user, setUser] = useState(DEFAULT_USER);
  const [project, setProject] = useState(DEFAULT_PROJECT);
  const [department, setDepartment] = useState(DEFAULT_DEPARTMENT);

  // Latest success response. Drives the success card. A failed submit is
  // surfaced separately via submitMutation.error so the form fields stay
  // editable and the user can retry without losing their input.
  const [result, setResult] = useState<ManualRequestResponse | null>(null);

  const submitMutation = useMutation({
    mutationFn: (req: ManualRequestReq) =>
      api<ManualRequestResp>('/v1/manual-requests', {
        method: 'POST',
        body: JSON.stringify(req),
      }),
    onSuccess: (resp) => {
      // toManualRequestResponse always returns at least one entry on a
      // successful allocation (internal/db/resource/resourceDB.go:763).
      const r = resp.manualRequestResponses?.[0];
      setResult(r ?? null);
      // Both the Resources list (aggregate usage) and the per-resource
      // Usages page need to reflect the new row.
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      queryClient.invalidateQueries({ queryKey: ['usages', resource.name] });
    },
  });

  // Per-type value validation. The server normalizes StringList (count →
  // regex) and Memory (text → bytes) for us, so we send the raw user input;
  // these checks only mirror what the server will accept on the wire.
  const valueValidation = useMemo<{ error?: string; hint?: string }>(() => {
    if (value.trim() === '') return { error: 'Required' };
    switch (type) {
      case 'LicenseCounter':
      case 'Collection':
      case 'Measurement': {
        const n = Number(value.trim());
        if (!Number.isInteger(n)) return { error: 'Must be a whole number' };
        if (n <= 0) return { error: 'Must be > 0' };
        return { hint: `${n} unit${n === 1 ? '' : 's'}` };
      }
      case 'StringList': {
        const n = Number(value.trim());
        if (!Number.isInteger(n)) return { error: 'Must be a whole number' };
        if (n <= 0) return { error: 'Must be > 0' };
        return { hint: `${n} item${n === 1 ? '' : 's'} from the list` };
      }
      case 'Double': {
        const n = Number(value.trim());
        if (!Number.isFinite(n)) return { error: 'Must be a number' };
        if (n <= 0) return { error: 'Must be > 0' };
        return { hint: `${n}` };
      }
      case 'Memory': {
        const bytes = parseMemoryBytes(value);
        if (bytes === null)
          return { error: 'Expected a value like 1GB, 512MiB, or 1024' };
        if (bytes <= 0) return { error: 'Must be > 0 bytes' };
        return { hint: `≈ ${formatBytes(bytes)} bytes` };
      }
      default:
        return {};
    }
  }, [type, value]);

  // Don't render the amount field red on open: only surface its validation
  // error once the user has typed something. While it's empty, Submit stays
  // disabled (canSubmit still uses the real valueValidation.error), so an
  // empty amount can't be sent — it just isn't flagged red, and the field's
  // help text shows instead.
  const showValueError = value.trim() !== '' && !!valueValidation.error;

  const fieldErrors = useMemo(() => {
    const errs: Partial<
      Record<'clusterId' | 'user' | 'project' | 'department', string>
    > = {};
    if (clusterId.trim() === '') errs.clusterId = 'Required';
    if (user.trim() === '') errs.user = 'Required';
    if (project.trim() === '') errs.project = 'Required';
    if (department.trim() === '') errs.department = 'Required';
    return errs;
  }, [clusterId, user, project, department]);

  const canSubmit = useMemo(() => {
    if (submitMutation.isPending) return false;
    if (valueValidation.error) return false;
    if (Object.keys(fieldErrors).length > 0) return false;
    return true;
  }, [submitMutation.isPending, valueValidation.error, fieldErrors]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setResult(null);
    submitMutation.mutate({
      name: resource.name,
      type,
      value: value.trim(),
      clusterId: clusterId.trim(),
      user: user.trim(),
      project: project.trim(),
      department: department.trim(),
    });
  };

  const handleRequestAnother = () => {
    setResult(null);
    submitMutation.reset();
    // Fresh cluster ID so the next allocation gets its own usage row.
    setClusterId(defaultClusterId());
  };

  const submitError = submitMutation.error
    ? extractApiMessage(submitMutation.error)
    : null;

  // Soft-limit warnings come through as `reason` on a successful response,
  // prefixed with "Warning:" (internal/db/resource/resourceDB.go:776-780).
  const successWarning =
    result && result.successful && result.reason ? result.reason : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <Section
        title="1. Resource"
        description="The resource the allocation will be recorded against."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Name" htmlFor="name">
            <input
              id="name"
              type="text"
              value={resource.name}
              readOnly
              className={cn(inputClass(false), 'bg-muted/40 cursor-not-allowed')}
            />
          </Field>
          <Field label="Type" htmlFor="type">
            <input
              id="type"
              type="text"
              value={type}
              readOnly
              className={cn(inputClass(false), 'bg-muted/40 cursor-not-allowed')}
            />
          </Field>
        </div>
      </Section>

      <Section
        title="2. Request"
        description="Amount to allocate and the identity it should be recorded under."
      >
        <div className="space-y-4">
          <Field
            label={valueLabel(type)}
            htmlFor="value"
            required
            error={showValueError ? valueValidation.error ?? null : null}
            hint={valueValidation.hint ?? null}
            help={valueHelp(type)}
          >
            <input
              id="value"
              type="text"
              inputMode={valueInputMode(type)}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className={inputClass(showValueError)}
              disabled={submitMutation.isPending}
              autoFocus
            />
          </Field>

          <Field
            label="Unique cluster ID"
            htmlFor="clusterId"
            required
            error={fieldErrors.clusterId ?? null}
            help="Per-allocation identifier. Used as the row key in usages — keep it unique so it doesn't collide with an existing allocation."
          >
            <textarea
              id="clusterId"
              value={clusterId}
              onChange={(e) => setClusterId(e.target.value)}
              rows={1}
              className={textareaClass(!!fieldErrors.clusterId)}
              disabled={submitMutation.isPending}
            />
          </Field>

          <div className="grid gap-4 md:grid-cols-3">
            <Field
              label="User"
              htmlFor="user"
              required
              error={fieldErrors.user ?? null}
            >
              <input
                id="user"
                type="text"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                className={inputClass(!!fieldErrors.user)}
                disabled={submitMutation.isPending}
              />
            </Field>
            <Field
              label="Project"
              htmlFor="project"
              required
              error={fieldErrors.project ?? null}
            >
              <input
                id="project"
                type="text"
                value={project}
                onChange={(e) => setProject(e.target.value)}
                className={inputClass(!!fieldErrors.project)}
                disabled={submitMutation.isPending}
              />
            </Field>
            <Field
              label="Department"
              htmlFor="department"
              required
              error={fieldErrors.department ?? null}
            >
              <input
                id="department"
                type="text"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className={inputClass(!!fieldErrors.department)}
                disabled={submitMutation.isPending}
              />
            </Field>
          </div>
        </div>
      </Section>

      {submitError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Request failed</div>
            <div className="text-xs mt-0.5 opacity-90">{submitError}</div>
          </div>
        </div>
      )}

      {result && result.successful && (
        <div
          className={cn(
            'rounded-md border p-3 text-sm flex items-start gap-2',
            successWarning
              ? 'border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-300'
              : 'border-primary/30 bg-primary/5 text-foreground',
          )}
        >
          <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="flex-1 space-y-1">
            <div className="font-medium">
              Allocated {result.grantedValue} on {resource.name}
            </div>
            <div className="text-xs opacity-80 font-mono">
              {result.timestamp}
            </div>
            {successWarning && (
              <div className="text-xs mt-1">{successWarning}</div>
            )}
            <div className="flex items-center gap-2 pt-2">
              <Link
                to={`/resources/${encodeURIComponent(resource.name)}/usages`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-primary text-primary-foreground hover:opacity-90"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View usages
              </Link>
              <button
                type="button"
                onClick={handleRequestAnother}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs border hover:bg-muted"
              >
                <RotateCw className="h-3.5 w-3.5" />
                Request another
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Link
          to="/resources"
          className="inline-flex items-center px-4 py-2 rounded-md text-sm border hover:bg-muted transition-colors"
        >
          Cancel
        </Link>
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {submitMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          {submitMutation.isPending ? 'Requesting…' : 'Request'}
        </button>
      </div>
    </form>
  );
}

function valueLabel(type: ResourceType): string {
  switch (type) {
    case 'StringList':
      return 'Number of items to allocate';
    case 'Memory':
      return 'Amount to allocate';
    case 'Double':
      return 'Amount to allocate';
    default:
      return 'Amount to allocate';
  }
}

function valueHelp(type: ResourceType): string {
  switch (type) {
    case 'LicenseCounter':
      return 'How many counters to grant.';
    case 'StringList':
      return 'How many items from the list to reserve. The server picks the specific entries.';
    case 'Collection':
      return 'How many collection items to reserve.';
    case 'Memory':
      return 'Memory amount, e.g. "1GB" or "512MiB" or "1024" (bytes).';
    case 'Double':
      return 'Numeric amount; decimals allowed.';
    case 'Measurement':
      return 'Number of license units to consume from the measured pool.';
    default:
      return '';
  }
}

function valueInputMode(type: ResourceType): 'numeric' | 'decimal' | 'text' {
  switch (type) {
    case 'Double':
      return 'decimal';
    case 'Memory':
      return 'text';
    default:
      return 'numeric';
  }
}
